import { motion } from "framer-motion";
import { Sprout, HeartHandshake, UsersRound, Globe2 } from "lucide-react";

const ripples = [
  {
    Icon: Sprout,
    title: "Emotionally secure children",
    body: "Kids who can name what they feel, regulate without shame, and trust the world a little more.",
  },
  {
    Icon: HeartHandshake,
    title: "Deeper family connections",
    body: "Homes where conversation goes past logistics and into what is actually being lived.",
  },
  {
    Icon: UsersRound,
    title: "Stronger, kinder communities",
    body: "The next generation of fathers won't be shaped in isolation but through shared experiences, support, and community.",
  },
  {
    Icon: Globe2,
    title: "A more compassionate world",
    body: "One emotionally-fluent child at a time, raising the floor for the generation after them.",
  },
];

export const Ripple = () => {
  return (
    <section
      id="ripple"
      data-testid="ripple-section"
      className="relative bg-[#F9F8F6] py-24 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-12 md:items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-5">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="font-serif-display text-[100px] md:text-[140px] lg:text-[180px] leading-[0.9] text-[#E8E3DA] font-light block mb-6 md:mb-8"
            >
              04
            </motion.span>
            <p className="text-[13px] md:text-sm uppercase tracking-[0.28em] text-[#C06E52] font-semibold mb-6">
              The Ripple Effect
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1 }}
              data-testid="ripple-heading"
              className="font-serif-display text-[#2D3730] text-4xl sm:text-5xl lg:text-[60px] leading-[1.08] font-light tracking-tight"
            >
              One father.
              <br />
              <span className="italic text-[#C06E52]">An entire lineage</span> changed.
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pb-2">
            <p className="text-[#5C665F] text-base md:text-lg leading-relaxed font-light">
              Fatherhood is not a private act. It moves outward — into classrooms, marriages,
              workplaces, and into the small future hands of grandchildren we may never meet.
              FabFathers is a movement because the work is bigger than any one home.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2D3730]/15">
          {ripples.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: i * 0.12 }}
              data-testid={`ripple-card-${i}`}
              className="bg-[#F9F8F6] p-8 md:p-10 flex flex-col gap-5 md:gap-8 hover:bg-[#F2EFEB] transition-colors duration-500"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#C06E52]/10 text-[#C06E52]">
                <r.Icon strokeWidth={1.5} className="w-6 h-6 md:w-7 md:h-7" />
              </span>
              <div>
                <h3 className="font-serif-display text-[#2D3730] text-2xl md:text-[28px] leading-snug mb-3 font-medium">
                  {r.title}
                </h3>
                <p className="text-[#5C665F] text-sm md:text-base leading-relaxed font-light">
                  {r.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="mt-20 md:mt-28 font-serif-display italic text-2xl md:text-4xl text-[#2D3730] max-w-[900px] leading-snug"
        >
          {`"FabFathers is not just about teaching parenting techniques. It is about helping fathers become active contributors to their children's emotional well-being—equipping them with the tools to build confidence, resilience, self-awareness, and emotional strength that their children will carry for the rest of their lives."`}
        </motion.p>
      </div>
    </section>
  );
};
